import axios from 'axios';
import React, { Fragment, useEffect, useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { IoMdArrowRoundBack } from 'react-icons/io';
import { HiShoppingBag } from 'react-icons/hi2';
import { FaLocationDot } from 'react-icons/fa6';
import { IoLogOutOutline } from 'react-icons/io5';

function UserProfilePage() {
    const navigate = useNavigate();
    const API_URL = process.env.REACT_APP_API_URL;
    const [mobile, setMobile] = useState('');
    const [userData, setUserData] = useState(null);
    const [orders, setOrders] = useState([]);
    const [loadingUser, setLoadingUser] = useState(true);
    const [loadingOrders, setLoadingOrders] = useState(true);

    useEffect(() => {
        const storedMobile = localStorage.getItem('mobile');
        if (!storedMobile) {
            navigate('/user-login'); // Redirect if mobile is not found
        } else {
            setMobile(storedMobile);
        }
    }, [navigate]);

    const handleUserData = async (cancelToken) => {
        if (!mobile) return;
        try {
            const response = await axios.get(`${API_URL}/user-details/${mobile}`,{ cancelToken });
            setUserData(response.data || {});
        } catch (err) {
            if (axios.isCancel && axios.isCancel(err)) return;
            console.error('Error fetching user data:', err);
        } finally {
            setLoadingUser(false);
        }
    };

    const handleOrders = async (cancelToken) => {
        if (!mobile) return;
        try {
            const res = await axios.get(`${API_URL}/order-data/${mobile}`, { cancelToken });
            const sorted = (res.data || []).sort((a,b)=> new Date(b.createdAt)-new Date(a.createdAt));
            setOrders(sorted);
        } catch (err) {
            if (axios.isCancel && axios.isCancel(err)) return;
            console.error('Error fetching orders:', err);
        } finally {
            setLoadingOrders(false);
        }
    };

    useEffect(() => {
        if (!mobile) return;
        const source = axios.CancelToken.source();
        handleUserData(source.token);
        handleOrders(source.token);
        return () => { source.cancel(); };
    }, [mobile]);

    const handleRemoveAddress = async (addressId) => {
        try {
            const userconfirm = window.confirm('Remove this address?');
            if (!userconfirm) return;
            await axios.delete(`${API_URL}/remove-address/${mobile}/${addressId}`);
            toast.success('Address removed successfully');
            // refresh user data
            handleUserData();
        } catch (error) {
            toast.error('Failed to remove address. Please try again.');
            console.error(error);
        }
    };

    const handleLogout = () => {
        const userconfirm = window.confirm('Are you sure you want to log out?');
        if (!userconfirm) return;
        localStorage.removeItem('mobile');
        navigate('/user-login');
    };

    // Derived values
    const totalOrders = orders.length;
    const totalAddresses = userData?.address ? userData.address.length : 0;
    const primaryName = userData?.address?.[0]?.name || 'User';
    const avatarInitial = (primaryName || 'U').trim().charAt(0).toUpperCase();
    const recentOrderDate = useMemo(() => {
        if (!orders[0]) return null;
        return new Date(orders[0].createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    }, [orders]);

    return (
        <Fragment>
            <div className='container-fluid m-t25'>
                <div className='row'>
                    <div className='col-12 cat-strip border shadow-sm'>
                        <div>
                            <button onClick={() => navigate(-1)}><IoMdArrowRoundBack /></button>
                            <label>My Profile</label>
                        </div>
                    </div>
                </div>
                {loadingUser && (
                    <div className='pt-4 pb-5 text-center'>
                        <div className='spinner-border text-danger' role='status'><span className='visually-hidden'>Loading...</span></div>
                    </div>
                )}
                {!loadingUser && userData && (
                    <>
                        <div className='userdash-header'>
                            <div className='ud-avatar'>{avatarInitial}</div>
                            <div className='ud-meta'>
                                <div className='ud-name'>{primaryName}</div>
                                <div className='ud-mobile'>+91 {userData.mobile}</div>
                                {recentOrderDate && <div className='ud-last small'>Last order: {recentOrderDate}</div>}
                            </div>
                        </div>
                        <div className='userdash-quick-grid'>
                            <Link to='/my-orders' className='udq-tile'>
                                <HiShoppingBag />
                                <span>My Orders</span>
                            </Link>
                            <Link to='/add-address' className='udq-tile'>
                                <FaLocationDot />
                                <span>Add Address</span>
                            </Link>
                            <button type='button' onClick={handleLogout} className='udq-tile danger'>
                                <IoLogOutOutline />
                                <span>Logout</span>
                            </button>
                        </div>
                        <div className='userdash-section-head'>Saved Addresses</div>
                        {totalAddresses ? (
                            <div className='userdash-address-list'>
                                {userData.address.map(addr => {
                                    const line = [addr.house, addr.area, addr.landmark, addr.city].filter(Boolean).join(', ');
                                    return (
                                        <div className='ud-addr-card' key={addr._id}>
                                            <div className='ud-addr-top'>
                                                <div className='ud-addr-name'>{addr.name || 'Name N/A'}</div>
                                                <button className='ud-addr-remove' onClick={() => handleRemoveAddress(addr._id)}>Remove</button>
                                            </div>
                                            <div className='ud-addr-line'>{line || '—'}</div>
                                            <div className='ud-addr-mobile'>Mobile: <span>{addr.mobile || userData.mobile}</span></div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className='userdash-empty'>No addresses added yet.</div>
                        )}
                    </>
                )}
            </div>
            <div className='mt-5 mb-5 pt-5'></div>
            <ToastContainer />
        </Fragment>
    );
}

export default UserProfilePage;
